import type { TFunction } from "i18next";
import type {
  PartnerRevenueAnalyticsApiPoint,
  PartnerRevenueAnalyticsApiResponse,
  PartnerRevenueAnalyticsBucket,
  PartnerRevenueAnalyticsChartPoint,
  PartnerRevenueAnalyticsPeriod,
} from "@/types/partner-revenue-analytics";

export type PartnerAnalyticsPeriodParams = {
  from: string;
  to: string;
  bucket: PartnerRevenueAnalyticsBucket;
};

function toDateParam(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function subtractDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() - days);
  return next;
}

function subtractMonths(date: Date, months: number): Date {
  const next = new Date(date);
  next.setMonth(next.getMonth() - months);
  return next;
}

export function getPartnerAnalyticsPeriodParams(
  period: PartnerRevenueAnalyticsPeriod,
  referenceDate: Date = new Date(),
): PartnerAnalyticsPeriodParams {
  const to = toDateParam(referenceDate);

  switch (period) {
    case "week":
      return {
        from: toDateParam(subtractDays(referenceDate, 7)),
        to,
        bucket: "day",
      };
    case "month":
      return {
        from: toDateParam(subtractMonths(referenceDate, 1)),
        to,
        bucket: "day",
      };
    case "sixMonths":
      return {
        from: toDateParam(subtractMonths(referenceDate, 6)),
        to,
        bucket: "month",
      };
    case "year":
    default:
      return {
        from: toDateParam(subtractMonths(referenceDate, 12)),
        to,
        bucket: "month",
      };
  }
}

function formatDayLabel(date: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
  }).format(date);
}

function formatWeekLabel(date: Date, locale: string, t: TFunction): string {
  return t("partners.detail.weekOfLabel", {
    date: formatDayLabel(date, locale),
  });
}

function formatMonthLabel(date: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    month: "long",
    year: "numeric",
  }).format(date);
}

export function formatPartnerAnalyticsPointLabel(
  dateIso: string,
  bucket: PartnerRevenueAnalyticsBucket,
  locale: string,
  t: TFunction,
): string {
  const date = new Date(dateIso);

  switch (bucket) {
    case "week":
      return formatWeekLabel(date, locale, t);
    case "month":
      return formatMonthLabel(date, locale);
    case "day":
    default:
      return formatDayLabel(date, locale);
  }
}

export function mapPartnerRevenueAnalyticsToChartPoints(
  response: PartnerRevenueAnalyticsApiResponse,
  locale: string,
  t: TFunction,
): PartnerRevenueAnalyticsChartPoint[] {
  return response.points.map((point: PartnerRevenueAnalyticsApiPoint) => ({
    label: formatPartnerAnalyticsPointLabel(
      point.date,
      response.bucket,
      locale,
      t,
    ),
    totalRevenue: point.totalRevenue,
    totalOrders: point.totalOrders,
    currency: point.currency,
  }));
}
